import { useState, useEffect, useCallback, useMemo } from 'react';
import {
	Modal,
	Input,
	Button,
	Checkbox,
	Typography,
	message,
	Steps,
	Select,
	Spin,
	Result,
} from 'antd';
import {
	LockOutlined,
	AppstoreOutlined,
	FileSearchOutlined,
	LinkOutlined,
	CheckCircleFilled,
} from '@ant-design/icons';
import './ApiWorkflowTemplate.scss';
import { useMutation } from '@tanstack/react-query';
import { updateUser } from '@/api/user';
import { debounce } from 'lodash';
import { Feature, ModalIntegrationProps } from './types';
import {
	checkGoHighLevelConnection,
	fetchGhlCalendars,
	openOAuthWindow,
	createGhlCalendarWorkflow,
	GhlCalendar,
} from './GoHighLevelIntegration';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';
import { safeJSONParse } from '@/utils/helper';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

// Define an interface for the step structure
interface StepItem {
	title: string;
	content: React.ReactNode;
	icon: React.ReactNode;
}

const appointmentDurationOptions = [
	{ value: 0.5, label: '30 minutes' },
	{ value: 1, label: '1 hour' },
	{ value: 1.5, label: '1 hour 30 minutes' },
	{ value: 2, label: '2 hours' },
	{ value: 2.5, label: '2 hours 30 minutes' },
	{ value: 3, label: '3 hours' },
	{ value: 3.5, label: '3 hours 30 minutes' },
	{ value: 4, label: '4 hours' },
	{ value: 4.5, label: '4 hours 30 minutes' },
	{ value: 5, label: '5 hours' },
];

const ModalIntegration: React.FC<ModalIntegrationProps> = ({
	open,
	selectedIntegration,
	features: featuresData,
	integrationType = 'authV1',
	instructionLink,
	description,
	authUrl,
	onClose,
	apiList = [],
	resellerEmailCheck,
	isGoHighLevel = false,
	username,
}) => {
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	// UI state (updates immediately)
	const [apiKeyInput, setApiKeyInput] = useState('');
	const [userIdInput, setUserIdInput] = useState('');

	// "Real" debounced creds
	const [apiKey, setApiKey] = useState('');
	const [userId, setUserId] = useState('');

	const [loading, setLoading] = useState(false);
	const [parsedFeatures, setParsedFeatures] = useState<Feature[]>([]);
	const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [currentStep, setCurrentStep] = useState(0);
	const [filteredApis, setFilteredApis] = useState<any[]>([]);

	const [isConnected, setIsConnected] = useState(false);
	const [isLoadingGhlCalendars, setIsLoadingGhlCalendars] = useState(false);
	const [ghlCalendars, setGhlCalendars] = useState<GhlCalendar[]>([]);
	const [selectedCalendar, setSelectedCalendar] = useState<string>('');
	const [selectedCalendarName, setSelectedCalendarName] = useState<string>('');
	const [appointmentDuration, setAppointmentDuration] = useState<number>(2);

	// Function to fetch GoHighLevel calendars - declare before using in useEffect
	const fetchGhlCalendarsData = useCallback(async () => {
		if (!isGoHighLevel || !resellerEmailCheck) {
			return;
		}

		try {
			setIsLoadingGhlCalendars(true);
			const calendars = await fetchGhlCalendars({
				resellerEmailCheck,
				agentId: selectedAgentId as string,
			});

			if (calendars && Array.isArray(calendars)) {
				setGhlCalendars(calendars);

				// Set first calendar as default if available
				if (calendars.length > 0) {
					setSelectedCalendar(calendars[0].id);
					setSelectedCalendarName(calendars[0].name);
				}
			}
		} catch (error) {
			console.error('Error fetching GHL calendars:', error);
			message.error('Failed to fetch GoHighLevel calendars');
		} finally {
			setIsLoadingGhlCalendars(false);
		}
	}, [isGoHighLevel]);

	// Effect to check GoHighLevel connection status
	useEffect(() => {
		if (isGoHighLevel && open && resellerEmailCheck) {
			const checkConnection = async () => {
				try {
					const connectionData = await checkGoHighLevelConnection({
						resellerEmailCheck,
						agentId: selectedAgentId as string,
					});
					setIsConnected(connectionData?.isConnected || false);

					if (connectionData?.isConnected) {
						fetchGhlCalendarsData();
					}
				} catch (error) {
					console.error('Error checking connection status:', error);
				}
			};
			checkConnection();
		}
	}, [isGoHighLevel, open, resellerEmailCheck, isConnected]);

	const debouncedSet = useMemo(
		() =>
			debounce(
				(key: string, id: string) => {
					setApiKey(key);
					setUserId(id);
				},
				500,
				{ leading: false, trailing: true },
			),
		[],
	);

	const handleApiKeyChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			setApiKeyInput(val);
			debouncedSet(val, userIdInput);
		},
		[userIdInput, debouncedSet],
	);

	const handleUserIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			setUserIdInput(val);
			debouncedSet(apiKeyInput, val);
		},
		[apiKeyInput, debouncedSet],
	);

	useEffect(() => {
		return () => {
			debouncedSet.cancel();
		};
	}, [debouncedSet]);

	useEffect(() => {
		try {
			if (typeof featuresData === 'string') {
				const parsed = safeJSONParse(featuresData, []);
				if (Array.isArray(parsed)) {
					setParsedFeatures(parsed);
				} else {
					setParsedFeatures([]);
				}
			} else if (Array.isArray(featuresData)) {
				setParsedFeatures(featuresData as Feature[]);
			} else if (featuresData && typeof featuresData === 'object') {
				// Convert object format to array format
				const features: Feature[] = Object.entries(featuresData).map(([name, ids]) => ({
					name,
					ids: Array.isArray(ids) ? ids : [],
				}));
				setParsedFeatures(features);
			} else {
				setParsedFeatures([]);
			}
		} catch (error) {
			console.error('Error parsing features:', error);
			setParsedFeatures([]);
		}
	}, [featuresData]);

	useEffect(() => {
		if (!parsedFeatures.length) {
			return;
		}

		const uniqueIds = new Set<number>();
		parsedFeatures.forEach((feat) => {
			if (selectedFeatures.includes(feat.name)) {
				feat.ids.forEach((id) => uniqueIds.add(id));
			}
		});

		const ids = Array.from(uniqueIds);

		const idsChanged = JSON.stringify(ids) !== JSON.stringify(selectedIds);

		if (idsChanged) {
			setSelectedIds(ids);

			const filteredApisList = apiList.filter((api) => {
				const apiId = typeof api.id === 'string' ? parseInt(api.id, 10) : api.id;
				return ids.some((id) => {
					const selectedId = typeof id === 'string' ? parseInt(id as string, 10) : id;
					return apiId === selectedId;
				});
			});

			setFilteredApis(filteredApisList);
		}
	}, [selectedFeatures, parsedFeatures, apiList]);

	const toggleFeatureSelection = (featureName: string) => {
		setSelectedFeatures((prev) =>
			prev.includes(featureName)
				? prev.filter((name) => name !== featureName)
				: [...prev, featureName],
		);
	};

	const validateAuthStep = () => {
		if (integrationType === 'basicAuth') {
			if (!userId.trim()) {
				message.error('Please enter a User ID');
				return false;
			}
			if (!apiKey.trim()) {
				message.error('Please enter an API key');
				return false;
			}
			return true;
		} else if (integrationType === 'authV1') {
			if (!apiKey.trim()) {
				message.error('Please enter an API key');
				return false;
			}
			return true;
		}
		return true;
	};

	const validateCalendarStep = () => {
		if (isGoHighLevel && !selectedCalendar) {
			message.error('Please select a calendar');
			return false;
		}
		return true;
	};

	const mutation = useMutation({
		mutationKey: ['update-client'],
		mutationFn: updateUser,
		onError: (error) => {
			message.error(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
		},
	});

	const processHeaders = (headers: any[], apiKeyValue: string) => {
		if (!headers || !Array.isArray(headers)) {
			return [];
		}

		return headers.map((header) => {
			if (header && typeof header.value === 'string' && header.value.includes('{{apikey}}')) {
				return {
					...header,
					value: header.value.replace('{{apikey}}', apiKeyValue),
				};
			}
			return header;
		});
	};

	const processAdditionalApis = (apis: any[], apiKeyValue: string) => {
		if (!apis || !Array.isArray(apis)) {
			return [];
		}

		return apis.map((api) => ({
			...api,
			headers: processHeaders(api.headers || [], apiKeyValue),
		}));
	};

	const handleCalendarChange = (value: string, option: any) => {
		setSelectedCalendar(value);
		setSelectedCalendarName(option.label);
	};

	const handleCreateCalendarWorkflow = async () => {
		try {
			setLoading(true);
			const loadingKey = 'creating-ghl-workflow';
			message.loading({ content: 'Integrating...', key: loadingKey, duration: 0 });

			await createGhlCalendarWorkflow(selectedCalendar, selectedCalendarName, username);

			message.success({
				content: `${selectedIntegration} integration is now live!`,
				key: loadingKey,
				duration: 5,
			});
			onClose();
		} catch (error) {
			console.error('Error creating calendar workflow:', error);
			message.error('Failed to create calendar workflow');
		} finally {
			setLoading(false);
		}
	};

	const handleActivate = async () => {
		if (isGoHighLevel) {
			handleCreateCalendarWorkflow();
			return;
		}

		if (!validateAuthStep()) {
			return;
		}

		if (selectedFeatures.length === 0) {
			message.error('Please select at least one feature');
			return;
		}

		let workflowApis = filteredApis;
		if (workflowApis.length === 0 && selectedIds.length > 0) {
			workflowApis = apiList.filter((api) => {
				const apiId = typeof api.id === 'string' ? parseInt(api.id, 10) : api.id;
				return selectedIds.some((id) => {
					const selectedId = typeof id === 'string' ? parseInt(id as string, 10) : id;
					return apiId === selectedId;
				});
			});
			setFilteredApis(workflowApis);
		}

		setLoading(true);
		try {
			const formattedWorkflows = workflowApis.map((api: any) => {
				let extractedCallInfo = api.extractedCallInfo;

				if (extractedCallInfo) {
					extractedCallInfo = extractedCallInfo.map((item: any) => {
						if (item.name === 'end_time') {
							return {
								...item,
								description: `Do not ask end date time form the caller. Add ${appointmentDuration} hour${appointmentDuration !== 1 ? 's' : ''} from start datetime.`,
							};
						}

						if (item.name === 'new_end_time') {
							return {
								...item,
								description: `Do not ask new end date time form the caller. Add ${appointmentDuration} hour${appointmentDuration !== 1 ? 's' : ''} from new start datetime.`,
							};
						}

						return item;
					});
				}

				return {
					id: api.id || String(Date.now()),
					name: api.name,
					workflowType: api.workflowType || 1,
					scenario: api.scenario || null,
					webhookInformation: {
						url: api.webhookInformation?.url,
						method: api.webhookInformation?.method || 'GET',
						body: api.webhookInformation?.body || '',
						headers: processHeaders(api.webhookInformation?.headers || [], apiKey),
						responseData: api.webhookInformation?.responseData || [],
					},
					authApi: {},
					isQueryParams: api.isQueryParams || false,
					isURLEncodedApi: api.isURLEncodedApi || false,
					isXChecksum: api.isXChecksum || false,
					xChecksumSalt: api.xChecksumSalt || '',
					xChecksumAlgorithm: api.xChecksumAlgorithm || '',
					xChecksumPayloadSorted: api.xChecksumPayloadSorted || false,
					extractedCallInfo,
					extractFromContext: api.extractFromContext || [],
					systemVariable: api.systemVariable || [],
					timeoutSeconds: api.timeoutSeconds || 10,
					speechToEmit: api.speechToEmit || '',
					isRepeatable: api.isRepeatable || false,
					postExecutionPrompt: api.postExecutionPrompt || '',
					requiresAuthToken: false,
					prompt: api.prompt || '',
					additionalApis: processAdditionalApis(api.additionalApis || [], apiKey),
				};
			});

			// Show loading message and send data to backend
			const loadingKey = 'saving-integration';
			message.loading({ content: 'Saving integration...', key: loadingKey, duration: 0 });

			// Save integration with the formatted workflows
			mutation.mutate(
				{
					call_webhook_workflows: JSON.stringify(formattedWorkflows),
					agentId: selectedAgentId as string,
				},
				{
					onSuccess: () => {
						message.success({
							content: `${selectedIntegration} integration is now live!`,
							key: loadingKey,
							duration: 5,
						});
						onClose();
					},
					onError: (error: { message: any }) => {
						message.error({
							content: `Failed to save integration: ${error instanceof Error ? error.message : 'Unknown error'}`,
							key: loadingKey,
							duration: 5,
						});
					},
				},
			);
		} catch (error) {
			console.error('Error activating integration:', error);
			message.error('Failed to activate integration');
		} finally {
			setLoading(false);
		}
	};

	const nextStep = () => {
		if (currentStep === 0) {
			if (isGoHighLevel) {
				if (!isConnected) {
					message.error('Please connect to GoHighLevel first');
					return;
				}
			}
		} else if (currentStep === 1) {
			if (isGoHighLevel) {
				if (!validateCalendarStep()) {
					return;
				}
			} else if (selectedFeatures.length === 0) {
				message.error('Please select at least one feature');
				return;
			}
		}

		setCurrentStep(currentStep + 1);
	};

	const prevStep = () => {
		setCurrentStep(currentStep - 1);
	};

	const handleGenericOAuthConnect = () => {
		if (authUrl) {
			if (isGoHighLevel) {
				openOAuthWindow(authUrl, true, 'gohighlevel');
			} else {
				openOAuthWindow(authUrl, true);
			}
		} else {
			message.error('OAuth URL is not configured for this integration');
		}
	};

	// Step content components for multi-step integrations
	const ApiKeyStep = () => (
		<div className="mt-6">
			<div className="mb-4">
				<Title level={4}>Step 1: Connect Your Account</Title>
				<Text type="secondary">
					{integrationType === 'oauth'
						? 'Connect your account securely'
						: 'Enter your API credentials to establish a secure connection'}
				</Text>
			</div>

			{description && <Paragraph className="mb-4 text-gray-600">{description}</Paragraph>}

			{integrationType === 'oauth' ? (
				<div className="flex justify-center mt-8">
					<Button
						type="primary"
						size="large"
						onClick={handleGenericOAuthConnect}
						icon={<LinkOutlined />}
						className="mb-4"
					>
						Connect to {selectedIntegration}
					</Button>
				</div>
			) : (
				<>
					{integrationType === 'basicAuth' && (
						<Input
							prefix={<LockOutlined />}
							placeholder="Enter your User ID"
							value={userIdInput}
							onChange={handleUserIdChange}
							className="mb-4"
						/>
					)}

					<Input.Password
						prefix={<LockOutlined />}
						placeholder="Enter your API key"
						value={apiKeyInput}
						onChange={handleApiKeyChange}
						className="mb-4"
					/>
				</>
			)}

			{instructionLink && (
				<Text className="block mb-4">
					<a href={instructionLink} target="_blank" rel="noopener noreferrer">
						Need help finding your {integrationType === 'basicAuth' ? 'credentials' : 'API key'}?
					</a>
				</Text>
			)}
		</div>
	);

	const FeaturesStep = () => {
		const hasEndTimeField = parsedFeatures.some((feature) => feature.field === 'end_time');

		return (
			<div className="mt-6">
				<div className="mb-4">
					<Title level={4}>Step 2: Select Features</Title>
					<Text type="secondary">
						Choose which features you want to integrate with your account
					</Text>
				</div>

				{/* Global duration setting */}
				{hasEndTimeField && (
					<div className="bg-gray-50 p-2 sm:p-4 mb-4 border rounded-md">
						<div className="flex items-center">
							<Text strong className="mr-3">
								Appointment Duration:
							</Text>
							<Select
								value={appointmentDuration}
								onChange={(value) => setAppointmentDuration(value)}
								className="w-1/3"
							>
								{appointmentDurationOptions.map(
									(item: { value: number; label: string }, index: number) => (
										<Select.Option key={index} value={item.value}>
											{item.label}
										</Select.Option>
									),
								)}
							</Select>
						</div>
					</div>
				)}

				<div className="space-y-4">
					{parsedFeatures.map((feature) => (
						<div
							key={feature.name}
							className={`border rounded-lg overflow-hidden transition-all ${
								selectedFeatures.includes(feature.name)
									? 'border-blue-500 shadow-md'
									: 'border-gray-200'
							}`}
						>
							{/* Feature header - always visible */}
							<div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50">
								<div className="flex items-center">
									<Checkbox
										checked={selectedFeatures.includes(feature.name)}
										onChange={(e) => {
											e.stopPropagation();
											toggleFeatureSelection(feature.name);
										}}
									/>
									<span className="ml-2 font-medium">{feature.name}</span>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	// GoHighLevel Calendar Selection Step
	const CalendarSelectionStep = () => (
		<div className="mt-6">
			<div className="mb-4">
				<Title level={4}>Step 2: Select GoHighLevel Calendar</Title>
				<Text type="secondary">
					Choose the calendar from your GoHighLevel account that you want to use for appointment
					scheduling.
				</Text>
			</div>

			{isLoadingGhlCalendars ? (
				<div className="text-center py-8">
					<Spin size="large" />
					<Paragraph className="mt-4">Loading available calendars...</Paragraph>
				</div>
			) : (
				<>
					{ghlCalendars.length > 0 ? (
						<div>
							<div className="mb-6">
								<label className="block mb-2">Select Calendar:</label>
								<Select
									placeholder="Choose a calendar"
									value={selectedCalendar}
									onChange={handleCalendarChange}
									className="w-full"
									loading={isLoadingGhlCalendars}
									disabled={isLoadingGhlCalendars}
								>
									{ghlCalendars.map((calendar) => (
										<Select.Option key={calendar.id} value={calendar.id} label={calendar.name}>
											{calendar.name}
										</Select.Option>
									))}
								</Select>
							</div>
						</div>
					) : (
						<div className="text-center py-8">
							<Text type="warning">No calendars found in your GoHighLevel account.</Text>
							<div className="mt-4">
								<Button onClick={fetchGhlCalendarsData}>Retry</Button>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);

	const ReviewStep = () => (
		<div className="mt-6">
			{isGoHighLevel ? (
				<Result
					status="success"
					icon={<CheckCircleFilled style={{ color: '#52c41a' }} />}
					title="Integration Complete!"
					subTitle={`Your GoHighLevel account has been successfully integrated with ${selectedCalendarName} calendar.`}
				/>
			) : (
				<>
					<div className="mb-4">
						<Title level={4}>Step 3: Review and Activate</Title>
						<Text type="secondary">Confirm your integration details before activating</Text>
					</div>

					<div className="bg-gray-50 p-4 rounded-lg mb-6">
						<div className="mb-3">
							<Text strong>Integration:</Text>
							<Text className="ml-2">{selectedIntegration}</Text>
						</div>

						{integrationType !== 'oauth' && (
							<>
								{integrationType === 'basicAuth' && (
									<div className="mb-3">
										<Text strong>User ID:</Text>
										<Text className="ml-2">••••••••••••{userId.slice(-4)}</Text>
									</div>
								)}

								<div className="mb-3">
									<Text strong>API Key:</Text>
									<Text className="ml-2">••••••••••••{apiKey.slice(-4)}</Text>
								</div>
							</>
						)}

						<div>
							<Text strong>Selected Features:</Text>
							<ul className="list-disc ml-8 mt-2">
								{selectedFeatures.map((feature) => (
									<li key={feature}>{feature}</li>
								))}
							</ul>
						</div>
					</div>

					<Paragraph className="text-gray-500 italic">
						Click "Activate Integration" to complete the setup process and create{' '}
						{filteredApis.length} API workflow(s).
					</Paragraph>
				</>
			)}
		</div>
	);

	const OAuthStep = () => (
		<div>
			<div className="mb-4">
				<Title level={4}>Step 1: Connect Your Account</Title>
				<Text type="secondary">Connect your account securely</Text>
			</div>

			{description && <Paragraph className="mb-4 text-gray-600">{description}</Paragraph>}

			<div className="flex flex-col items-center justify-center">
				{isGoHighLevel && isConnected ? (
					<>
						<div className="text-center mb-6">
							<CheckCircleFilled style={{ fontSize: '48px', color: '#52c41a' }} />
							<Paragraph className="mt-2 text-green-600 font-medium">
								Successfully Connected!
							</Paragraph>
						</div>
					</>
				) : (
					<Button
						type="primary"
						size="large"
						onClick={handleGenericOAuthConnect}
						icon={<LinkOutlined />}
						className="mb-6 px-8"
					>
						Connect to {selectedIntegration}
					</Button>
				)}
			</div>

			{instructionLink && (
				<Text className="block mt-8 text-center">
					<a href={instructionLink} target="_blank" rel="noopener noreferrer">
						Need help? View integration instructions
					</a>
				</Text>
			)}
		</div>
	);

	let stepsToShow: StepItem[] = [];

	if (integrationType === 'oauth') {
		if (isGoHighLevel) {
			stepsToShow = [
				{
					title: 'Connect',
					content: <OAuthStep />,
					icon: <LinkOutlined />,
				},
				{
					title: 'Calendar',
					content: <CalendarSelectionStep />,
					icon: <AppstoreOutlined />,
				},
				{
					title: 'Complete',
					content: <ReviewStep />,
					icon: <FileSearchOutlined />,
				},
			];
		} else {
			stepsToShow = [
				{
					title: 'Connect',
					content: <OAuthStep />,
					icon: <LinkOutlined />,
				},
			];
		}
	} else {
		stepsToShow = [
			{
				title: 'API Key',
				content: <ApiKeyStep />,
				icon: <LockOutlined />,
			},
			{
				title: 'Features',
				content: <FeaturesStep />,
				icon: <AppstoreOutlined />,
			},
			{
				title: 'Review',
				content: <ReviewStep />,
				icon: <FileSearchOutlined />,
			},
		];
	}

	const modalTitle = (
		<div className="flex items-center">
			<span>{`Integrate with ${selectedIntegration}`}</span>
		</div>
	);

	useEffect(() => {
		const handleOAuthComplete = (event: CustomEvent) => {
			const detail = event.detail || {};
			if (detail.source === 'gohighlevel' && detail.success) {
				setIsConnected(true);
				fetchGhlCalendarsData();
			}
		};
		window.addEventListener('oauth-complete', handleOAuthComplete as EventListener);
		return () => {
			window.removeEventListener('oauth-complete', handleOAuthComplete as EventListener);
		};
	}, [fetchGhlCalendarsData]);

	return (
		<Modal
			title={modalTitle}
			open={open}
			onCancel={onClose}
			footer={null}
			width={window.innerWidth <= 768 ? '100%' : '50%'}
			style={{
				top: 0,
				height: '100vh',
				margin: 0,
				paddingBottom: 0,
				position: 'absolute',
				right: 0,
				animation: 'slideInRight 0.3s forwards',
			}}
			styles={{
				body: {
					height: 'calc(100vh - 55px)',
					padding: 0,
					overflow: 'auto',
				},
				mask: {
					animation: 'fadeIn 0.3s forwards',
				},
			}}
			className="custom-drawer-modal"
			wrapClassName="custom-modal-wrap"
		>
			<div className="flex h-full">
				<div className="w-1/4 border-r border-gray-200 p-6 bg-gray-50">
					<Steps current={currentStep} direction="vertical" className="h-full">
						{stepsToShow.map((item) => (
							<Step key={item.title} title={item.title} icon={item.icon} />
						))}
					</Steps>
				</div>

				{/* Right column - Content */}
				<div className="w-3/4 p-6 overflow-auto">
					{stepsToShow[currentStep]?.content}

					<div className="flex justify-end mt-6">
						{currentStep > 0 && (
							<Button onClick={prevStep} className="mr-2">
								Back
							</Button>
						)}

						{currentStep < stepsToShow.length - 1 && (
							<Button type="primary" onClick={nextStep}>
								Next
							</Button>
						)}

						{currentStep === stepsToShow.length - 1 && (
							<Button type="primary" onClick={handleActivate} loading={loading}>
								Activate Integration
							</Button>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default ModalIntegration;
